import React from 'react'
import {Alert} from 'react-bootstrap'
import ModalComponent from '../CustomModal'
import {ErrorCircle} from '../../icons/ErrorCircle'
import {KTSVG} from '../../../../template/_metronic/helpers'

/**
 * `CustomMessage` es un componente de React que muestra mensajes de éxito, error o advertencia
 * en un modal o una alerta.
 * 
 * El componente puede mostrar alertas con estilos específicos o un modal personalizable usando 
 * `ModalComponent`. También puede incluir una función opcional para cerrar el mensaje.
 * 
 * @param {ICustomMessage} props - Las propiedades del componente.
 * @param {string | undefined} props.title - Título del mensaje que se muestra en el modal o alerta.
 * @param {string} [props.description] - Descripción o contenido detallado del mensaje.
 * @param {'success' | 'danger' | 'warning'} [props.messageType='success'] - Tipo de mensaje que determina el estilo visual.
 * @param {'alert' | 'modal'} [props.displayType='alert'] - Determina si el mensaje se muestra como alerta o modal.
 * @param {() => void} [props.close] - Función opcional para ejecutar al cerrar el mensaje.
 * @returns {React.FC<ICustomMessage>} Un componente funcional de React que muestra mensajes personalizados.
 * 
 * @example
 * // Ejemplo de uso como una alerta:
 * <CustomMessage
 *   title="Operación Exitosa"
 *   description="La operación se ha completado correctamente."
 *   messageType="success"
 *   displayType="alert"
 *   close={() => console.log('Alerta cerrada')}
 * />
 * 
 * // Ejemplo de uso como un modal:
 * <CustomMessage
 *   title="Advertencia"
 *   description="Se ha producido un error inesperado."
 *   messageType="danger"
 *   displayType="modal"
 *   close={() => console.log('Modal cerrado')}
 * />
 */


interface ICustomMessage {
  title: string | undefined
  description?: string
  messageType?: 'success' | 'danger' | 'warning'
  displayType?: 'alert' | 'modal'
  close?: () => void
}

const CustomMessage = ({
  title,
  description,
  messageType = 'success',
  displayType = 'alert',
  close,
}: ICustomMessage) => {
  const variant = messageType === 'danger' || messageType === 'warning' ? 'danger' : 'primary'
  return (
    <>
      {description && (
        <>
          {displayType === 'modal' ? (
            <ModalComponent
              size={'md'}
              variant={variant}
              textConfirm={'Aceptar'}
              handleConfirm={close}
              backdrop='static'
              closeButton={false}
            >
              <div className='d-flex flex-center flex-column px-6'>
                {messageType === 'danger' && (
                  <div className='mb-6'>
                    <ErrorCircle />
                  </div>
                )}
                {messageType === 'success' && (
                  <div className='mb-6'>
                    <KTSVG
                      path='/media/icons/duotune/general/gen043.svg'
                      className='svg-icon-5x svg-icon-success'
                    />
                  </div>
                )}
                <div className='fs-1 fw-semibold mb-1 text-center'>{title || ''}</div>
                <div className='fs-4 fw-semibold text-gray text-center'>{description}</div>
              </div>
            </ModalComponent>
          ) : (
            <>
              <Alert
                variant={messageType}
                className={`mb-4 mt-2 text-center`}
                onClose={close}
                dismissible
              >
                {title && <h2 className='alert-heading'>{title}</h2>}
                <p className={`fw-semibold ${title ? '' : 'mt-4'}`}>{description}</p>
              </Alert>
            </>
          )}
        </>
      )}
    </>
  )
}

export default CustomMessage
